import type { AudioEvent, AudioEventMap, Handler, Unsubscribe } from '../../ports/audio';

/**
 * Tipiziran oddajnik dogodkov, skupen obema implementacijama `AudioPort`.
 *
 * Notranje hrani poslušalce netipizirano, ker `Handler<'error'>` ni
 * dodeljiv `Handler<AudioEvent>` (parametri so kontravariantni). Oba
 * pretvorbena koraka sta tu in nikjer drugje; zunanja površina je tipizirana.
 */

type OpaqueHandler = (payload: unknown) => void;

export interface AudioEmitter {
  on<E extends AudioEvent>(event: E, fn: Handler<E>): Unsubscribe;
  emit<E extends AudioEvent>(event: E, payload: AudioEventMap[E]): void;
  clear(): void;
}

export function createAudioEmitter(): AudioEmitter {
  const listeners = new Map<AudioEvent, Set<OpaqueHandler>>();

  return {
    on(event, fn) {
      const opaque = fn as unknown as OpaqueHandler;
      let set = listeners.get(event);
      if (set === undefined) {
        set = new Set();
        listeners.set(event, set);
      }
      set.add(opaque);

      return () => {
        listeners.get(event)?.delete(opaque);
      };
    },

    emit(event, payload) {
      const set = listeners.get(event);
      if (set === undefined) return;
      // Kopija: poslušalec se sme med oddajo odjaviti.
      for (const fn of [...set]) fn(payload);
    },

    clear() {
      listeners.clear();
    },
  };
}
